/**
 * Where Follow puts the page (requirements 0103 and 0104). The app has the same
 * rule in Dart (`mobile/lib/screens/reader_screen.dart`, `_reveal`); a change
 * to the numbers here is a change there.
 *
 * Only the vertical position is measured, so the text's direction does not
 * matter.
 */

/** A vertical extent in window coordinates, as getBoundingClientRect gives. */
export type Span = { top: number; bottom: number };

/** The eye reads from the top of the text area to this far down it. */
const BAND_END = 0.75;
/** A word that left the band has its line brought this far down the area. */
const BAND_LINE = 0.25;

/**
 * fitsCentred says whether a sentence centred in the window is wholly inside
 * the readable area: clear of whatever is pinned over the text at either end.
 * One that is not is followed by its spoken word instead.
 */
export function fitsCentred(sentenceHeight: number, area: Span, windowHeight: number): boolean {
  const covered = Math.max(area.top, windowHeight - area.bottom);
  return sentenceHeight <= windowHeight - 2 * covered;
}

/**
 * followWord is how far to scroll, in pixels, to keep the spoken word on
 * screen: nothing while it is inside the band, and otherwise the distance that
 * brings its line a quarter of the way down. The page therefore moves about
 * half a readable area at a time, never once a word.
 */
export function followWord(word: Span, area: Span): number | null {
  const height = area.bottom - area.top;
  if (height <= 0) return null;
  if (word.top >= area.top && word.bottom <= area.top + height * BAND_END) return null;
  const delta = Math.round(word.top - (area.top + height * BAND_LINE));
  return delta === 0 ? null : delta;
}
